import type { IFieldDefinition, IRecordListParams, IRecordTypeDefinition, PageSize } from '@platform/core/types';
import type { IMdQuerySpec } from './IMdQuerySpec';

const SYSTEM = new Set(['module', 'limit', 'columns', 'sort', 'title', 'open', 'overdue']);
const yes = (v: string | undefined): boolean => /^(yes|true|так|1)$/i.test((v ?? '').trim());
const SIZES: PageSize[] = [10, 15, 25, 50, 100];

/** Розбір рядків «ключ: значення» живого запиту (прототип kbQuery). Чиста функція. */
export function mdQuerySpec(lines: string[]): IMdQuerySpec {
  const raw: Record<string, string> = {};
  lines.forEach((l) => { const i = l.indexOf(':'); if (i > 0) raw[l.slice(0, i).trim().toLowerCase()] = l.slice(i + 1).trim(); });
  const limit = Math.max(1, Math.min(50, parseInt(raw['limit'] ?? '10', 10) || 10));
  const columns = raw['columns'] ? raw['columns'].split(',').map((c) => c.trim()).filter(Boolean) : ['title', 'status', 'assignee', 'due'];
  return {
    module: (raw['module'] ?? 'task').toLowerCase(), open: yes(raw['open']), overdue: yes(raw['overdue']), limit, columns,
    ...(raw['sort'] ? { sort: raw['sort'] } : {}),
    filters: Object.keys(raw).filter((k) => !SYSTEM.has(k)).map((key) => ({ key, value: raw[key]! })),
  };
}

/**
 * Параметри API списку для живого запиту: open/overdue — статуси, не закриті; overdue — термін до сьогодні; «assignee: me» — поточний
 * користувач; інші умови — рівність значення поля (за ключем або англійським підписом); розмір сторінки — найменший, що вміщує ліміт.
 */
export function mdQueryParams(spec: IMdQuerySpec, rt: IRecordTypeDefinition, meId: string, today: string): IRecordListParams {
  const parts: string[] = [];
  const open = rt.statuses.list.filter((s) => !s.closed).map((s) => encodeURIComponent(s.value));
  if ((spec.open || spec.overdue) && open.length) parts.push(`status:in:${open.join(',')}`);
  if (spec.overdue) parts.push(`due:lt:${today}`);
  const fieldOf = (k: string): IFieldDefinition | undefined => rt.fields.find((f) => f.key.toLowerCase() === k || (f.label?.en ?? '').toLowerCase() === k);
  spec.filters.forEach(({ key, value }) => {
    const f = fieldOf(key);
    if (!f) return;
    const v = f.type === 'user' && value.toLowerCase() === 'me' ? meId : value;
    parts.push(`${f.key}:${f.type === 'tags' ? 'has' : 'eq'}:${encodeURIComponent(v)}`);
  });
  return {
    pageSize: SIZES.find((s) => s >= spec.limit) ?? 50,
    ...(parts.length ? { filter: parts.join(';') } : {}),
    ...(spec.sort ? { sort: spec.sort } : {}),
  };
}
